/** @jsx jsx */
import type { PropsWithChildren, SyntheticEvent } from 'react';
import { jsx } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import type { Permalink } from '@atlassian/jira-issue-view-common/src/component/permalink-button/permalink-provider/index.tsx';
import { ModalContextProvider } from '@atlassian/jira-modal-context-provider/src/ModalContextProvider.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCommentContext } from './CommentContextProvider.tsx';

interface ContainerBaseProps {
	onMouseOver?: (e: SyntheticEvent) => void;
	onMouseOut?: (e: SyntheticEvent) => void;
	onFocus?: (e: SyntheticEvent) => void;
	onBlur?: (e: SyntheticEvent) => void;
	children: React.ReactNode;
}
const ContainerBase = ({
	children,
	onMouseOver,
	onMouseOut,
	onFocus,
	onBlur,
}: ContainerBaseProps) => (
	<Box
		onMouseOver={onMouseOver}
		onMouseOut={onMouseOut}
		onFocus={onFocus}
		onBlur={onBlur}
		testId="issue-comment-base.ui.comment.custom-comment.container"
	>
		{children}
	</Box>
);

type ContainerProps<T = {}> = T &
	PropsWithChildren<{
		permalink: Permalink;
	}>;
export const ThreadedCommentContainer = ({ children }: ContainerProps) => {
	const { setOptimisticCommentContainer } = useCommentContext();
	return (
		<ContainerBase>
			<div ref={setOptimisticCommentContainer}>{children}</div>
		</ContainerBase>
	);
};

// Cleanup with issue_create_from_comment_experiment
export const FloatingMenuContainer = ({ children, permalink }: ContainerProps) => {
	const { setOptimisticCommentContainer } = useCommentContext();
	return (
		<ContainerBase
			onMouseOver={(e) => showPermalink(e, permalink)}
			onMouseOut={(e) => hidePermalink(e, permalink)}
			onFocus={(e) => showPermalink(e, permalink)}
			onBlur={(e) => hidePermalink(e, permalink)}
		>
			<div ref={setOptimisticCommentContainer}>{children}</div>
		</ContainerBase>
	);
};
export const OptimisticCommentContainer = ({ children }: ContainerProps) => {
	const { setOptimisticCommentContainer } = useCommentContext();
	if (fg('jira_comment_alignment_fix')) {
		return (
			<Box xcss={optimisticNonThreadedCommentStyles} ref={setOptimisticCommentContainer}>
				{children}
			</Box>
		);
	}
	return <div ref={setOptimisticCommentContainer}>{children}</div>;
};
const CommentContainer = ({ children, permalink }: ContainerProps) => {
	const { setCommentPermalinkContainer, commentId } = useCommentContext();
	return (
		<ContainerBase
			onMouseOver={(e) => showPermalink(e, permalink)}
			onMouseOut={(e) => hidePermalink(e, permalink)}
			onFocus={(e) => showPermalink(e, permalink)}
			onBlur={(e) => hidePermalink(e, permalink)}
		>
			<span data-testid={`comment-base-item-${commentId}`} ref={setCommentPermalinkContainer}>
				<ModalContextProvider>{children}</ModalContextProvider>
			</span>
		</ContainerBase>
	);
};
export default CommentContainer;

const optimisticNonThreadedCommentStyles = xcss({
	marginLeft: 'space.100',
});

const showPermalink = (e: SyntheticEvent, permalink: Permalink) => {
	e.stopPropagation();
	permalink.show();
};
const hidePermalink = (e: SyntheticEvent, permalink: Permalink) => {
	e.stopPropagation();
	permalink.hide();
};
